// src/components/tiptap/extensions/PerformanceInterruptionNode.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import { supabase } from "../../../config/supabase";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../../../common/SafeIcon.jsx";

const {
  FiZap,
  FiSave,
  FiTrash2,
  FiPlay,
  FiSquare,
  FiVolume2,
  FiVolumeX,
  FiClock,
} = FiIcons;

const cls = (...xs) => xs.filter(Boolean).join(" ");

const INTERRUPT_CUE_URL =
  "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/tools/audio/pig/pig-interrupt.wav";

const RESUME_CUE_URL =
  "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/tools/audio/pig/pig-restart.wav";

const PIG_IMAGE_URL =
  "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/tools/images/pig/pig.svg";

// Frequency → random interval between distractions (ms)
const FREQUENCY_INTERVALS = {
  frequent: { min: 8000, max: 15000 },
  medium: { min: 15000, max: 30000 },
  infrequent: { min: 30000, max: 45000 },
};

const DEFAULT_FREQUENCY = "medium";

// helper to allow form controls to keep default behavior
const isFormControl = (el) =>
  el &&
  (el.tagName === "SELECT" ||
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.isContentEditable);

// Pick a random integer between min and max (inclusive)
const randomBetween = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

/* ─────────────────────────────────────────────────────────────
   PERF: per-page caches (safe; does not change functionality)
   - Cache the PIG library fetch across mounts
   - Cache decoded WebAudio buffers by URL
   - Cache HTMLAudioElements by URL for one-shots
   ──────────────────────────────────────────────────────────── */

const pigLibraryCache = {
  data: null,
  categories: null,
  fetchedAt: 0,
};
let pigLibraryInflight = null;

async function fetchPigLibraryOnce() {
  // 5 min TTL is conservative; you can adjust later if needed.
  if (
    pigLibraryCache.data &&
    Date.now() - pigLibraryCache.fetchedAt < 5 * 60 * 1000
  ) {
    return { data: pigLibraryCache.data, categories: pigLibraryCache.categories };
  }

  if (!pigLibraryInflight) {
    pigLibraryInflight = (async () => {
      const { data, error } = await supabase
        .from("pig_interruptions_library")
        .select("*")
        .eq("is_active", true);

      pigLibraryInflight = null;
      if (error) throw error;

      const cats = Array.from(
        new Set((data || []).map((r) => r.category).filter(Boolean))
      ).sort();

      pigLibraryCache.data = data || [];
      pigLibraryCache.categories = cats;
      pigLibraryCache.fetchedAt = Date.now();
      return { data: pigLibraryCache.data, categories: pigLibraryCache.categories };
    })();
  }

  return pigLibraryInflight;
}

// Cache decoded loop buffers by URL (dedupe fetch+decode)
const audioBufferCache = new Map(); // url -> Promise<AudioBuffer|null>
function getLoopBuffer(url) {
  if (!url) return Promise.resolve(null);
  const existing = audioBufferCache.get(url);
  if (existing) return existing;
  const p = loadAudioBuffer(url).catch(() => null);
  audioBufferCache.set(url, p);
  return p;
}

// Cache one-shot audio elements by URL (reuse objects)
const audioElementCache = new Map(); // url -> HTMLAudioElement
function getAudioEl(url) {
  if (!url) return null;
  let a = audioElementCache.get(url);
  if (!a) {
    try {
      a = new Audio(url);
      a.preload = "auto";
      audioElementCache.set(url, a);
    } catch {
      return null;
    }
  }
  return a;
}

// ─────────────────────────────────────────────────────────────
// Gapless looping via Web Audio API
// ─────────────────────────────────────────────────────────────
let audioCtx = null;

async function loadAudioBuffer(url) {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioCtx = new Ctx();
  }
  const res = await fetch(url);
  const arr = await res.arrayBuffer();
  return await audioCtx.decodeAudioData(arr);
}

async function playLoopingBuffer(buffer) {
  if (!buffer || typeof window === "undefined") return null;
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioCtx = new Ctx();
  }
  try {
    await audioCtx.resume();
  } catch {
    // ignore
  }
  const source = audioCtx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  source.connect(audioCtx.destination);
  source.start(0);
  return source;
}

function PerformanceInterruptionBlock({
  node,
  updateAttributes,
  editor,
  deleteNode,
}) {
  const attrs = node.attrs || {};
  const ctx = editor?.storage?.mms ?? {};
  const saved = !!attrs.recordId;

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [frequency, setFrequency] = useState(
    attrs.frequency || DEFAULT_FREQUENCY
  );
  const [sfxMuted, setSfxMuted] = useState(!!attrs.sfxMuted);

  // Library of possible interruptions
  const [interruptions, setInterruptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Runtime state
  const [isRunning, setIsRunning] = useState(false);
  const [mode, setMode] = useState("idle"); // 'idle' | 'performing' | 'interruption'
  const [currentInterruption, setCurrentInterruption] = useState(null);
  const [nextDelayMs, setNextDelayMs] = useState(null);
  const [interruptionSecondsRemaining, setInterruptionSecondsRemaining] =
    useState(null); // countdown during interruption

  const nextTimeoutRef = useRef(null);
  const interruptionTimeoutRef = useRef(null); // auto-resume timer
  const countdownRef = useRef(null); // 1-second countdown tick

  // Non-looping audio (plain HTMLAudioElement)
  const audioRef = useRef(null);

  // Cue sounds
  const interruptCueRef = useRef(null);
  const resumeCueRef = useRef(null);

  // Gapless loop state
  const loopSourceRef = useRef(null); // AudioBufferSourceNode
  const isRandomSoundRef = useRef(false);

  // Ensure we have a noteBlockId & defaults
  useEffect(() => {
    if (!attrs.noteBlockId) {
      updateAttributes({
        noteBlockId: crypto.randomUUID(),
        frequency: DEFAULT_FREQUENCY,
        sfxMuted: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load PIG library (cached per page; scheduled at idle when possible)
  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        const res = await fetchPigLibraryOnce();
        if (!alive) return;
        setInterruptions(res.data || []);
        setCategories(res.categories || []);
      } catch {
        // ignore
      }
    };

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const id = window.requestIdleCallback(run, { timeout: 1500 });
      return () => {
        alive = false;
        try {
          window.cancelIdleCallback(id);
        } catch {
          // ignore
        }
      };
    }

    const t = setTimeout(run, 0);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, []);

  // Preload interrupt / resume cue sounds
  useEffect(() => {
    try {
      interruptCueRef.current = new Audio(INTERRUPT_CUE_URL);
    } catch {
      interruptCueRef.current = null;
    }
    try {
      resumeCueRef.current = new Audio(RESUME_CUE_URL);
    } catch {
      resumeCueRef.current = null;
    }
  }, []);

  // Cleanup on unmount: timeouts + audio
  useEffect(
    () => () => {
      if (nextTimeoutRef.current) clearTimeout(nextTimeoutRef.current);
      if (interruptionTimeoutRef.current)
        clearTimeout(interruptionTimeoutRef.current);
      if (countdownRef.current) clearTimeout(countdownRef.current);

      if (audioRef.current) {
        try {
          audioRef.current.pause();
        } catch {
          // ignore
        }
        audioRef.current = null;
      }
      if (interruptCueRef.current) {
        try {
          interruptCueRef.current.pause();
        } catch {
          // ignore
        }
        interruptCueRef.current = null;
      }
      if (resumeCueRef.current) {
        try {
          resumeCueRef.current.pause();
        } catch {
          // ignore
        }
        resumeCueRef.current = null;
      }
      if (loopSourceRef.current) {
        try {
          loopSourceRef.current.stop();
        } catch {
          // ignore
        }
        loopSourceRef.current = null;
      }
    },
    []
  );

  // Mute/unmute mid-game: pause/stop any active audio when muting
  useEffect(() => {
    if (sfxMuted) {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
        } catch {
          // ignore
        }
      }
      if (interruptCueRef.current) {
        try {
          interruptCueRef.current.pause();
        } catch {
          // ignore
        }
      }
      if (resumeCueRef.current) {
        try {
          resumeCueRef.current.pause();
        } catch {
          // ignore
        }
      }
      if (loopSourceRef.current) {
        try {
          loopSourceRef.current.stop();
        } catch {
          // ignore
        }
        loopSourceRef.current = null;
      }
    }
  }, [sfxMuted]);

  const filteredInterruptions = useMemo(() => {
    if (!interruptions.length) return [];
    if (!selectedCategories.length) return interruptions;
    const set = new Set(selectedCategories);
    return interruptions.filter((r) => r.category && set.has(r.category));
  }, [interruptions, selectedCategories]);

  const canPlay = useMemo(
    () => saved && filteredInterruptions.length > 0,
    [saved, filteredInterruptions.length]
  );

  const getIntervalForFrequency = (freq) => {
    const config = FREQUENCY_INTERVALS[freq] || FREQUENCY_INTERVALS.medium;
    return randomBetween(config.min, config.max);
  };

  // Whenever we're "running" and in performing mode, schedule the next interruption.
  useEffect(() => {
    if (
      !isRunning ||
      mode !== "performing" ||
      filteredInterruptions.length === 0
    ) {
      setNextDelayMs(null);
      if (nextTimeoutRef.current) {
        clearTimeout(nextTimeoutRef.current);
        nextTimeoutRef.current = null;
      }
      return;
    }

    const delay = getIntervalForFrequency(frequency);
    setNextDelayMs(delay);

    if (nextTimeoutRef.current) {
      clearTimeout(nextTimeoutRef.current);
    }

    const timeoutId = setTimeout(() => {
      triggerInterruption();
    }, delay);
    nextTimeoutRef.current = timeoutId;

    return () => {
      if (nextTimeoutRef.current) {
        clearTimeout(nextTimeoutRef.current);
        nextTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, mode, filteredInterruptions, frequency]);

  // Countdown tick during an interruption
  useEffect(() => {
    if (mode !== "interruption" || interruptionSecondsRemaining == null) {
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
        countdownRef.current = null;
      }
      return;
    }

    if (interruptionSecondsRemaining <= 0) {
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
        countdownRef.current = null;
      }
      return;
    }

    countdownRef.current = setTimeout(() => {
      setInterruptionSecondsRemaining((prev) =>
        typeof prev === "number" ? Math.max(prev - 1, 0) : prev
      );
    }, 1000);

    return () => {
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [mode, interruptionSecondsRemaining]);

  const finishInterruption = () => {
    setCurrentInterruption(null);
    setInterruptionSecondsRemaining(null);
    isRandomSoundRef.current = false;
    setMode("performing");
  };

  const resumeFromInterruption = async () => {
    // Clear any pending auto-resume or countdown
    if (interruptionTimeoutRef.current) {
      clearTimeout(interruptionTimeoutRef.current);
      interruptionTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearTimeout(countdownRef.current);
      countdownRef.current = null;
    }
    setInterruptionSecondsRemaining(null);

    // Stop any looping/main audio
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch {
        // ignore
      }
      audioRef.current = null;
    }
    if (loopSourceRef.current) {
      try {
        loopSourceRef.current.stop();
      } catch {
        // ignore
      }
      loopSourceRef.current = null;
    }

    const isRandomSound = isRandomSoundRef.current;

    // Play restart cue (except for random sound), then finish
    try {
      if (!sfxMuted && resumeCueRef.current && !isRandomSound) {
        const cue = resumeCueRef.current;
        cue.pause();
        cue.currentTime = 0;
        cue.play().catch(() => {});
      }
    } catch {
      // ignore
    }

    finishInterruption();
  };

  const triggerInterruption = async () => {
    if (!isRunning || filteredInterruptions.length === 0) return;

    const idx = Math.floor(Math.random() * filteredInterruptions.length);
    const intr = filteredInterruptions[idx];
    setCurrentInterruption(intr);
    setMode("interruption");
    setNextDelayMs(null);

    // Clear any existing timers/audio
    if (nextTimeoutRef.current) {
      clearTimeout(nextTimeoutRef.current);
      nextTimeoutRef.current = null;
    }
    if (interruptionTimeoutRef.current) {
      clearTimeout(interruptionTimeoutRef.current);
      interruptionTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearTimeout(countdownRef.current);
      countdownRef.current = null;
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch {
        // ignore
      }
      audioRef.current = null;
    }
    if (loopSourceRef.current) {
      try {
        loopSourceRef.current.stop();
      } catch {
        // ignore
      }
      loopSourceRef.current = null;
    }

    const durationSec =
      typeof intr?.duration_seconds === "number" && intr.duration_seconds > 0
        ? intr.duration_seconds
        : 5;

    // Normalize category so we treat "random sound", "Random-sound", etc. as the same
    const normalizedCategory = (intr?.category || "")
      .toLowerCase()
      .replace(/\s+/g, "-")
      .trim();
    const isRandomSound = normalizedCategory === "random-sound";

    // PERF/BUGFIX: persist for resume logic
    isRandomSoundRef.current = isRandomSound;

    // Helper: start the interruption countdown + auto-resume timer
    const startCountdown = () => {
      if (!durationSec || durationSec <= 0) return;

      setInterruptionSecondsRemaining(durationSec);

      if (interruptionTimeoutRef.current) {
        clearTimeout(interruptionTimeoutRef.current);
      }
      interruptionTimeoutRef.current = setTimeout(() => {
        resumeFromInterruption();
      }, durationSec * 1000);
    };

    // For random-sound interruptions, keep existing behaviour:
    // countdown starts as soon as the interruption begins.
    if (isRandomSound) {
      startCountdown();
    }

    // Prepare main audio
    let mainMode = null; // 'loop' | 'single' | null
    let loopBuffer = null;

    if (!sfxMuted && intr?.audio_url) {
      if (intr.loop_audio) {
        // Use gapless loop via Web Audio (cached buffer)
        try {
          loopBuffer = await getLoopBuffer(intr.audio_url);
          if (loopBuffer) mainMode = "loop";
        } catch {
          loopBuffer = null;
        }
      } else {
        // One-shot HTMLAudio (cached element)
        const audio = getAudioEl(intr.audio_url);
        if (audio) {
          audioRef.current = audio;
          mainMode = "single";
        } else {
          audioRef.current = null;
        }
      }
    }

    // If there's no usable audio for a NON random-sound interruption,
    // treat "audio finished" as "now" and start the timer immediately.
    if (!mainMode && !isRandomSound) {
      startCountdown();
    }

    // Play interrupt cue, then main audio
    try {
      const startMainAudio = async () => {
        if (sfxMuted || !mainMode) return;

        if (mainMode === "loop" && loopBuffer) {
          loopSourceRef.current = await playLoopingBuffer(loopBuffer);

          // For non-random + loop_audio, there is no natural "end",
          // so we start the countdown when the loop starts.
          if (!isRandomSound && durationSec > 0) {
            startCountdown();
          }
        } else if (mainMode === "single" && audioRef.current) {
          const audio = audioRef.current;

          // Ensure consistent replay when using cached elements
          try {
            audio.pause();
            audio.currentTime = 0;
          } catch {
            // ignore
          }

          if (!isRandomSound) {
            // For non–random-sound interruptions: only start timer
            // AFTER the audio has finished playing
            audio.onended = () => {
              audio.onended = null;
              startCountdown();
            };
          } else {
            // Avoid retaining old handlers between plays
            audio.onended = null;
          }

          audio.play().catch(() => {
            // If playback fails, fall back to "audio finished now" behaviour
            if (!isRandomSound) {
              if (audio.onended) audio.onended = null;
              startCountdown();
            }
          });
        }
      };

      if (!sfxMuted && interruptCueRef.current) {
        const cue = interruptCueRef.current;
        cue.pause();
        cue.currentTime = 0;

        cue.onended = () => {
          cue.onended = null;
          startMainAudio();
        };

        cue.play().catch(() => {
          cue.onended = null;
          startMainAudio();
        });
      } else {
        // No cue (muted or missing): start main audio immediately
        startMainAudio();
      }
    } catch {
      // ignore audio errors; countdown / auto-resume still handle flow
    }
  };

  const handleStart = () => {
    if (!canPlay) {
      if (!saved) {
        alert("Please save PIG before starting.");
      } else if (!filteredInterruptions.length) {
        alert(
          "No interruptions available yet. Ask your teacher to add some to the PIG library."
        );
      }
      return;
    }

    setIsRunning(true);
    setMode("performing");

    // Clear any stale timers/audio; the effect will schedule a fresh one
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch {
        // ignore
      }
      audioRef.current = null;
    }
    if (loopSourceRef.current) {
      try {
        loopSourceRef.current.stop();
      } catch {
        // ignore
      }
      loopSourceRef.current = null;
    }
    if (nextTimeoutRef.current) {
      clearTimeout(nextTimeoutRef.current);
      nextTimeoutRef.current = null;
    }
    if (interruptionTimeoutRef.current) {
      clearTimeout(interruptionTimeoutRef.current);
      interruptionTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearTimeout(countdownRef.current);
      countdownRef.current = null;
    }
    setInterruptionSecondsRemaining(null);
    isRandomSoundRef.current = false;
  };

  const handleStop = () => {
    setIsRunning(false);
    setMode("idle");
    setCurrentInterruption(null);
    setNextDelayMs(null);
    setInterruptionSecondsRemaining(null);
    isRandomSoundRef.current = false;

    if (nextTimeoutRef.current) {
      clearTimeout(nextTimeoutRef.current);
      nextTimeoutRef.current = null;
    }
    if (interruptionTimeoutRef.current) {
      clearTimeout(interruptionTimeoutRef.current);
      interruptionTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearTimeout(countdownRef.current);
      countdownRef.current = null;
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch {
        // ignore
      }
      audioRef.current = null;
    }
    if (interruptCueRef.current) {
      try {
        interruptCueRef.current.pause();
      } catch {
        // ignore
      }
    }
    if (resumeCueRef.current) {
      try {
        resumeCueRef.current.pause();
      } catch {
        // ignore
      }
    }
    if (loopSourceRef.current) {
      try {
        loopSourceRef.current.stop();
      } catch {
        // ignore
      }
      loopSourceRef.current = null;
    }
  };

  const handleSave = async () => {
    const effectiveLessonNoteId = attrs.lessonNoteId ?? ctx.lessonNoteId ?? null;
    if (!effectiveLessonNoteId) {
      alert("Missing context: need lessonNoteId to save this tool.");
      return;
    }

    const nbid = attrs.noteBlockId ?? crypto.randomUUID();
    const schoolId = attrs.schoolId ?? ctx.schoolId ?? null;
    const teacherId = attrs.teacherId ?? ctx.userId ?? null;
    const studentId = attrs.studentId ?? ctx.studentId ?? null;
    const studentMasterId = attrs.studentMasterId ?? ctx.masterStudentId ?? null;
    const createdBy = ctx.userId ?? null;

    setSaving(true);
    const { data, error } = await supabase.rpc("get_or_create_pig_instance", {
      p_note_block_id: nbid,
      p_school_id: schoolId,
      p_lesson_note_id: effectiveLessonNoteId,
      p_created_by: createdBy,
      p_teacher_id: teacherId,
      p_student_id: studentId,
      p_student_master_id: studentMasterId,
      p_frequency: frequency || DEFAULT_FREQUENCY,
    });
    setSaving(false);

    if (error) {
      alert(error.message);
      return;
    }

    updateAttributes({
      recordId: data.id,
      lessonNoteId: effectiveLessonNoteId,
      schoolId,
      teacherId,
      studentId,
      studentMasterId,
      noteBlockId: nbid,
      frequency: frequency || DEFAULT_FREQUENCY,
      sfxMuted,
    });
  };

  const handleDelete = async () => {
    if (!attrs.recordId) {
      deleteNode();
      return;
    }
    if (!confirm("Delete this PIG tool from the note?")) return;

    setDeleting(true);
    const { error } = await supabase.rpc("delete_pig_instance", {
      p_id: attrs.recordId,
    });
    setDeleting(false);

    if (error) {
      alert(error.message);
      return;
    }
    deleteNode();
  };

  const frequencyLabel = (f) =>
    f === "frequent"
      ? "Frequent (lots of interruptions)"
      : f === "infrequent"
      ? "Infrequent (occasional interruptions)"
      : "Medium (balanced)";

  const humanNextDelay = useMemo(() => {
    if (!nextDelayMs) return null;
    const seconds = Math.round(nextDelayMs / 1000);
    if (seconds < 15) return `${seconds} seconds`;
    const mins = (seconds / 60).toFixed(1);
    return `${mins} min`;
  }, [nextDelayMs]);

  const toggleCategory = (cat) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  return (
    <NodeViewWrapper
      as="div"
      contentEditable={false}
      role="group"
      tabIndex={-1}
      className="relative group w-full rounded-2xl bg-slate-50 shadow-sm p-4 border border-slate-200"
      onMouseDownCapture={(e) => {
        if (!isFormControl(e.target)) e.preventDefault();
        e.stopPropagation();
      }}
      onTouchStartCapture={(e) => {
        if (!isFormControl(e.target)) e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiZap} className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <div className="text-lg font-bold text-slate-800">
                Performance Interruption Generator
              </div>
              {/* Smaller pig icon for very small screens */}
              <img
                src={PIG_IMAGE_URL}
                alt="PIG mascot"
                className="sm:hidden w-8 h-8 pointer-events-none select-none"
              />
            </div>
            <div className="text-sm text-slate-500">
              Random distractions to test focus while performing.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            className="p-2 rounded-full text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
            onClick={() => {
              const next = !sfxMuted;
              setSfxMuted(next);
              updateAttributes({ sfxMuted: next });
            }}
            title={sfxMuted ? "Unmute sound" : "Mute sound"}
          >
            <SafeIcon icon={sfxMuted ? FiVolumeX : FiVolume2} className="w-5 h-5" />
          </button>
          <button
            className={cls(
              "px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all flex items-center gap-2",
              saved
                ? "bg-slate-200 text-slate-500 border-slate-200 cursor-default"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-slate-400"
            )}
            disabled={saving || saved}
            onClick={handleSave}
          >
            <SafeIcon icon={FiSave} />
            {saved ? "Saved" : saving ? "Saving..." : "Save"}
          </button>
          <button
            className="p-2 rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 transition-colors opacity-50 group-hover:opacity-100"
            disabled={deleting}
            onClick={handleDelete}
            title="Delete Tool"
          >
            <SafeIcon icon={FiTrash2} className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Config + Controls */}
      <div className="bg-white/80 rounded-xl p-4 border border-dashed border-slate-200 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          <div className="md:col-span-2 space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-2 block">
                Interruption frequency
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                {["frequent", "medium", "infrequent"].map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={cls(
                      "flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all",
                      frequency === f
                        ? "bg-orange-50 border-orange-400 text-orange-700"
                        : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                    )}
                    onClick={() => {
                      setFrequency(f);
                      updateAttributes({ frequency: f });
                    }}
                    disabled={isRunning}
                  >
                    {frequencyLabel(f)}
                  </button>
                ))}
              </div>
            </div>

            {categories.length > 0 && (
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-2 block">
                  Categories
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={cls(
                      "px-3 py-1.5 rounded-full border text-xs font-medium transition-all",
                      selectedCategories.length === 0
                        ? "bg-orange-50 border-orange-400 text-orange-700"
                        : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                    )}
                    onClick={() => setSelectedCategories([])}
                    disabled={isRunning}
                  >
                    All
                  </button>
                  {categories.map((cat) => {
                    const active = selectedCategories.includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        className={cls(
                          "px-3 py-1.5 rounded-full border text-xs font-medium transition-all",
                          active
                            ? "bg-orange-50 border-orange-400 text-orange-700"
                            : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                        )}
                        onClick={() => {
                          if (isRunning) return;
                          toggleCategory(cat);
                        }}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="flex md:justify-end items-end">
            <button
              type="button"
              className={cls(
                "w-full md:w-auto px-6 py-2.5 rounded-xl text-base font-semibold text-white shadow-lg transition-all transform flex items-center justify-center gap-2",
                isRunning
                  ? "bg-red-500 hover:bg-red-600 active:scale-95"
                  : canPlay
                  ? "bg-orange-500 hover:bg-orange-600 active:scale-95"
                  : "bg-slate-400 cursor-not-allowed"
              )}
              onClick={isRunning ? handleStop : handleStart}
              disabled={!canPlay && !isRunning}
              title={
                !saved
                  ? "You must save the tool before using it"
                  : !filteredInterruptions.length
                  ? "No interruptions in the PIG library (or matching categories) yet"
                  : isRunning
                  ? "Stop PIG"
                  : "Start PIG"
              }
            >
              <SafeIcon icon={isRunning ? FiSquare : FiPlay} />
              {isRunning ? "Stop" : "Begin"}
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
          <div>
            {isRunning ? (
              mode === "performing" ? (
                <span>Student should be actively performing now.</span>
              ) : mode === "interruption" ? (
                interruptionSecondsRemaining != null ? (
                  <span>
                    Interruption in progress. Resuming playing in{" "}
                    <span className="font-semibold">
                      {interruptionSecondsRemaining} second
                      {interruptionSecondsRemaining === 1 ? "" : "s"}
                    </span>
                    .
                  </span>
                ) : (
                  <span>An interruption is in progress…</span>
                )
              ) : null
            ) : (
              <span>
                When you press <span className="font-semibold">Begin</span>, PIG
                will randomly trigger interruptions while the student plays.
              </span>
            )}
          </div>
          {isRunning && humanNextDelay && mode === "performing" && (
            <div className="flex items-center gap-1">
              <SafeIcon icon={FiClock} className="w-4 h-4" />
              <span>Next interruption in about {humanNextDelay}</span>
            </div>
          )}
        </div>
      </div>

      {/* Live display */}
      <div className="rounded-xl p-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white min-h-[160px] flex items-center justify-center relative overflow-hidden">
        {/* Pig sitting on top of the black box, to the right */}
        <img
          src={PIG_IMAGE_URL}
          alt="PIG mascot"
          className="hidden sm:block absolute right-6 top-1/2 transform -translate-y-1/2 w-24 h-24 pointer-events-none select-none drop-shadow-lg"
        />

        {mode === "interruption" && currentInterruption ? (
          <div className="flex flex-col items-center text-center gap-3 max-w-md">
            {currentInterruption.image_url && (
              <div className="w-32 h-32 rounded-2xl overflow-hidden border border-white/20 shadow-lg bg-black/20">
                <img
                  src={currentInterruption.image_url}
                  alt={currentInterruption.label || "Interruption"}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="uppercase text-xs tracking-wide text-orange-200">
              INTERRUPTION!
            </div>
            <div className="text-lg font-semibold">
              {currentInterruption.label || "Follow the instructions!"}
            </div>
            {currentInterruption.command_text && (
              <div className="text-sm text-slate-100">
                {currentInterruption.command_text}
              </div>
            )}
            {typeof currentInterruption.duration_seconds === "number" && (
              <div className="text-xs text-slate-300">
                Do this for about {currentInterruption.duration_seconds} seconds.
              </div>
            )}
            {interruptionSecondsRemaining != null && (
              <div className="mt-2 text-xs text-orange-100">
                Resuming playing in{" "}
                <span className="font-semibold">
                  {interruptionSecondsRemaining} second
                  {interruptionSecondsRemaining === 1 ? "" : "s"}
                </span>
                …
              </div>
            )}
            <button
              type="button"
              className="mt-3 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-sm font-semibold shadow-md transition-colors"
              onClick={resumeFromInterruption}
            >
              Resume playing now
            </button>
          </div>
        ) : mode === "performing" ? (
          <div className="flex flex-col items-center text-center gap-2">
            <div className="uppercase text-xs tracking-wide text-orange-200">
              KEEP PLAYING
            </div>
            <div className="text-2xl font-semibold">Continue performing!</div>
            <div className="text-sm text-slate-200 max-w-md">
              Focus on steady rhythm and sound. PIG will surprise you with
              interruptions.
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center gap-2">
            <div className="uppercase text-xs tracking-wide text-orange-200">
              READY TO GO
            </div>
            <div className="text-2xl font-semibold">Press Begin to start PIG.</div>
            <div className="text-sm text-slate-200 max-w-md">
              The student plays continuously. PIG will randomly interrupt them
              with silly actions and sounds.
            </div>
          </div>
        )}

        {/* Decorative blob */}
        <div className="pointer-events-none absolute -bottom-16 -right-16 w-48 h-48 bg-orange-500/30 rounded-full blur-3xl" />
      </div>
    </NodeViewWrapper>
  );
}

const PerformanceInterruptionNode = Node.create({
  name: "performanceInterruption",
  group: "block",
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      noteBlockId: { default: null },
      lessonNoteId: { default: null },
      schoolId: { default: null },
      teacherId: { default: null },
      studentId: { default: null },
      studentMasterId: { default: null },
      recordId: { default: null },
      frequency: { default: DEFAULT_FREQUENCY },
      sfxMuted: { default: false },
    };
  },
  parseHTML() {
    return [{ tag: "performance-interruption" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["performance-interruption", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PerformanceInterruptionBlock);
  },
});

export default PerformanceInterruptionNode;